<template>
  <div class="template-list-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <h2>发货单模板管理</h2>
        <p>管理和配置发货单模板，支持自定义字段和样式</p>
      </div>
      <div class="header-actions">
        <el-button type="primary" @click="createTemplate">
          <i class="el-icon-plus"></i>
          新建模板
        </el-button>
      </div>
    </div>

    <!-- 搜索区域 -->
    <div class="search-area">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="模板名称">
          <el-input 
            v-model="searchForm.name" 
            placeholder="请输入模板名称"
            clearable
            style="width: 200px;"
          ></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 模板列表 -->
    <div class="template-grid">
      <div 
        v-for="template in filteredTemplates" 
        :key="template.id"
        class="template-card"
      >
        <div class="card-header">
          <div class="template-name">{{ template.name }}</div>
          <div class="template-actions">
            <el-dropdown @command="handleCommand" trigger="click">
              <span class="el-dropdown-link">
                <i class="el-icon-more"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :command="{action: 'preview', template}">预览</el-dropdown-item>
                <el-dropdown-item :command="{action: 'edit', template}">编辑</el-dropdown-item>
                <el-dropdown-item :command="{action: 'copy', template}">复制</el-dropdown-item>
                <el-dropdown-item :command="{action: 'delete', template}" divided>删除</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
        
        <div class="card-content">
          <div class="template-description">
            {{ template.description || '暂无描述' }}
          </div>
          
          <div class="template-preview" @click="previewTemplate(template)">
            <div class="preview-content" v-html="getPreviewHtml(template)"></div>
          </div>
          
          <div class="template-info">
            <div class="info-item">
              <span class="label">创建时间:</span>
              <span class="value">{{ formatDate(template.createdAt) }}</span>
            </div>
            <div class="info-item">
              <span class="label">更新时间:</span>
              <span class="value">{{ formatDate(template.updatedAt) }}</span>
            </div>
          </div>
        </div>
        
        <div class="card-footer">
          <el-button size="small" @click="editTemplate(template)">编辑</el-button>
          <el-button size="small" type="primary" @click="viewTemplateDetail(template)">查看详情</el-button>
        </div>
      </div>
      
      <!-- 空状态 -->
      <div v-if="filteredTemplates.length === 0" class="empty-state">
        <i class="el-icon-document-add"></i>
        <p>暂无模板，点击右上角按钮创建新模板</p>
      </div>
    </div>

    <!-- 预览对话框 -->
    <el-dialog 
      title="模板预览" 
      :visible.sync="previewVisible" 
      width="80%" 
      top="5vh"
      class="preview-dialog"
    >
      <div class="preview-actions">
        <el-button @click="exportPreviewPdf" type="primary">导出PDF</el-button>
        <el-button @click="previewVisible = false">关闭</el-button>
      </div>
      <div class="preview-container">
        <div class="preview-content" v-html="previewContent"></div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'TemplateList',
  data() {
    return {
      searchForm: {
        name: ''
      },
      templates: [],
      previewVisible: false,
      previewContent: '',
      currentPreviewTemplate: null,
      
      // 示例数据用于预览
      sampleData: {
        info: {
          company: '江西壹号农业科技有限公司',
          customerName: '示例客户',
          deliveryDate: '2024-01-15',
          orderNo: 'SO202401150001',
          address: '江西省南昌市红谷滩新区',
          contact: '张三',
          phone: '13800138000',
          totalAmount: '299.50',
          deliveryPerson: '李四',
          deliveryTime: '2024-01-15 14:30',
          remark: '请注意保鲜'
        },
        list: [
          { index: 1, name: '苹果', spec: '红富士', unit: '斤', quantity: 10, price: 8.50, amount: 85.00, remark: '新鲜' },
          { index: 2, name: '香蕉', spec: '进口', unit: '斤', quantity: 5, price: 6.80, amount: 34.00, remark: '' },
          { index: 3, name: '橙子', spec: '脐橙', unit: '斤', quantity: 8, price: 12.50, amount: 100.00, remark: '甜' }
        ]
      }
    }
  },
  
  computed: {
    filteredTemplates() {
      if (!this.searchForm.name) {
        return this.templates
      }
      return this.templates.filter(template => 
        template.name.toLowerCase().includes(this.searchForm.name.toLowerCase())
      )
    }
  },
  
  created() {
    this.loadTemplates()
  },
  
  methods: {
    // 加载模板列表
    loadTemplates() {
      const templates = JSON.parse(localStorage.getItem('deliveryTemplates') || '[]')
      this.templates = templates.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    },
    
    // 搜索
    handleSearch() {
      // 搜索逻辑已在computed中处理
    },
    
    // 重置搜索
    resetSearch() {
      this.searchForm.name = ''
    },
    
    // 创建新模板
    createTemplate() {
      this.$router.push('/delivery/template-designer')
    },
    
    // 编辑模板
    editTemplate(template) {
      this.$router.push({
        path: '/delivery/template-designer',
        query: { id: template.id }
      })
    },
    
    // 使用模板
    viewTemplateDetail(template) {
      // 打开详情页面，显示使用真实数据的模板
      this.$router.push({
        path: '/delivery/template-detail',
        query: { 
          templateId: template.id,
          mode: 'preview'
        }
      })
    },
    
    // 预览模板
    previewTemplate(template) {
      this.currentPreviewTemplate = template
      
      // 如果有完整HTML（包含CSS），则使用它
      if (template.fullHtml) {
        this.previewContent = this.renderTemplate(template.fullHtml, this.sampleData)
      } else {
        // 兼容旧版模板
        this.previewContent = this.renderTemplate(template.content, this.sampleData)
      }
      
      this.previewVisible = true
    },
    
    // 获取预览HTML（用于卡片显示）
    getPreviewHtml(template) {
      // 简化版预览，只显示部分内容
      const content = template.fullHtml || template.content
      // 使用renderTemplate方法，但不替换占位符
      const rendered = this.renderTemplate(content)
      
      return `
        <style>
          .mini-preview { font-size: 10px; transform: scale(0.3); transform-origin: top left; width: 333%; height: 333%; overflow: hidden; }
          .mini-preview .template-header { text-align: center; margin-bottom: 10px; }
          .mini-preview .header-title { font-weight: bold; margin-bottom: 5px; }
          .mini-preview .goods-table { width: 100%; border-collapse: collapse; }
          .mini-preview .goods-table th, .mini-preview .goods-table td { border: 1px solid #000; padding: 2px; text-align: center; font-size: 8px; }
        </style>
        <div class="mini-preview">${rendered}</div>
      `
    },
    
    // 渲染模板
    renderTemplate(templateHtml, data) {
      let rendered = templateHtml
      
      // 检查是否是完整HTML（包含CSS）
      const isFullHtml = templateHtml.includes('<!DOCTYPE html>') || templateHtml.includes('<html')
      
      // 在预览模式下，不替换占位符，保持原样显示
      // 如果不是完整HTML，添加基本样式
      if (!isFullHtml) {
        rendered = `
          <style>
            .delivery-template { font-family: SimSun, serif; font-size: 12px; }
            .template-header { text-align: center; margin-bottom: 20px; }
            .header-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
            .header-info { display: flex; flex-wrap: wrap; justify-content: space-between; }
            .info-item { margin: 5px 0; }
            .goods-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .goods-table th, .goods-table td { border: 1px solid #000; padding: 8px; text-align: center; }
            .goods-table th { background-color: #f5f5f5; font-weight: bold; }
            .text-right { text-align: right; }
            .total-amount { font-weight: bold; }
            .template-footer { margin-top: 30px; }
            .footer-info { display: flex; flex-wrap: wrap; justify-content: space-between; }
            .footer-item { margin: 5px 0; }
          </style>
          ${rendered}
        `
      }
      
      return rendered
    },
    
    // 下拉菜单命令处理
    handleCommand(command) {
      const { action, template } = command
      
      switch (action) {
        case 'preview':
          this.previewTemplate(template)
          break
        case 'edit':
          this.editTemplate(template)
          break
        case 'copy':
          this.copyTemplate(template)
          break
        case 'delete':
          this.deleteTemplate(template)
          break
      }
    },
    
    // 复制模板
    copyTemplate(template) {
      const newTemplate = {
        ...template,
        id: Date.now().toString(),
        name: template.name + ' - 副本',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      
      const templates = JSON.parse(localStorage.getItem('deliveryTemplates') || '[]')
      templates.push(newTemplate)
      localStorage.setItem('deliveryTemplates', JSON.stringify(templates))
      
      this.loadTemplates()
      this.$message.success('模板复制成功')
    },
    
    // 删除模板
    deleteTemplate(template) {
      this.$confirm('确定要删除这个模板吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const templates = JSON.parse(localStorage.getItem('deliveryTemplates') || '[]')
        const index = templates.findIndex(t => t.id === template.id)
        if (index !== -1) {
          templates.splice(index, 1)
          localStorage.setItem('deliveryTemplates', JSON.stringify(templates))
          this.loadTemplates()
          this.$message.success('删除成功')
        }
      }).catch(() => {
        // 用户取消删除
      })
    },
    
    // 导出预览PDF
    exportPreviewPdf() {
      if (this.currentPreviewTemplate) {
        // 获取完整HTML内容
        const template = this.currentPreviewTemplate
        const content = template.fullHtml || template.content
        // 使用renderTemplate方法，但不替换占位符
        const renderedHtml = this.renderTemplate(content)
        
        // 创建一个临时的iframe来打印内容
        const printIframe = document.createElement('iframe')
        printIframe.style.position = 'absolute'
        printIframe.style.top = '-9999px'
        printIframe.style.left = '-9999px'
        document.body.appendChild(printIframe)
        
        printIframe.onload = () => {
          try {
            // 写入HTML内容
            const doc = printIframe.contentDocument || printIframe.contentWindow.document
            doc.open()
            doc.write(renderedHtml)
            doc.close()
            
            // 等待样式和图片加载完成
            setTimeout(() => {
              try {
                // 调用打印功能
                printIframe.contentWindow.print()
                
                // 打印完成后移除iframe
                setTimeout(() => {
                  document.body.removeChild(printIframe)
                }, 1000)
              } catch (e) {
                console.error('打印失败:', e)
                this.$message.error('打印失败，请稍后重试')
                document.body.removeChild(printIframe)
              }
            }, 500)
          } catch (e) {
            console.error('准备打印内容失败:', e)
            this.$message.error('准备打印内容失败，请稍后重试')
            document.body.removeChild(printIframe)
          }
        }
        
        // 处理加载错误
        printIframe.onerror = () => {
          this.$message.error('加载打印内容失败，请稍后重试')
          document.body.removeChild(printIframe)
        }
      }
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN') + ' ' + date.toLocaleTimeString('zh-CN', { hour12: false })
    }
  }
}
</script>

<style scoped>
.template-list-container {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.header-left h2 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 24px;
}

.header-left p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.search-area {
  margin-bottom: 20px;
  padding: 16px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.search-form {
  margin: 0;
}

.template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.template-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
}

.template-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.template-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.template-actions {
  cursor: pointer;
  color: #666;
  font-size: 18px;
}

.template-actions:hover {
  color: #409eff;
}

.card-content {
  padding: 20px;
}

.template-description {
  color: #666;
  font-size: 14px;
  margin-bottom: 16px;
  min-height: 20px;
}

.template-preview {
  height: 200px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  margin-bottom: 16px;
  position: relative;
}

.template-preview:hover {
  border-color: #409eff;
}

.preview-content {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.template-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.info-item .label {
  color: #666;
}

.info-item .value {
  color: #333;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

.preview-dialog .preview-actions {
  margin-bottom: 20px;
  text-align: right;
}

.preview-dialog .preview-container {
  border: 1px solid #ddd;
  padding: 20px;
  background: white;
  max-height: 70vh;
  overflow: auto;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .template-grid {
    grid-template-columns: 1fr;
  }
  
  .page-header {
    flex-direction: column;
    gap: 16px;
  }
  
  .header-actions {
    align-self: stretch;
  }
}
</style>
